import { useCallback, memo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { doUpsertAiConversationSuggestions } from 'ducks/ai/operations'
import { selectSuggestionForType } from 'ducks/ai/selectors/suggestions'

import Button from '@groovehq/internal-design-system/lib/components/Button/Button'
import SuggestedTags from '../SuggestedTags'
import Summary from '../Summary'
import Sentiment from '../Sentiment'
import BetaSignUp from '../BetaSignUp'
import RegenerateButton from '../RegenerateButton'

import { styles as baseStyles } from '../styles'

const Content = ({ ticketId, accountId, canUseClassifications }) => {
  const dispatch = useDispatch()
  const sentimentSuggestion = useSelector(state =>
    selectSuggestionForType(state, ticketId, { type: 'sentiment' })
  )
  const summarySuggestion = useSelector(state =>
    selectSuggestionForType(state, ticketId, { type: 'summary' })
  )
  const tagsSuggestion = useSelector(state =>
    selectSuggestionForType(state, ticketId, { type: 'tags' })
  )

  const onGenerateReducedOverviewClick = useCallback(
    () => {
      dispatch(
        doUpsertAiConversationSuggestions({
          suggestionTypes: ['sentiment', 'tags'],
          ticketId,
        })
      )
    },
    [dispatch, ticketId]
  )

  const onGenerateFullOverviewClick = useCallback(
    () => {
      dispatch(
        doUpsertAiConversationSuggestions({
          suggestionTypes: ['sentiment', 'summary', 'tags'],
          ticketId,
        })
      )
    },
    [dispatch, ticketId]
  )

  if (!canUseClassifications) {
    return (
      <>
        {summarySuggestion.suggestion.id && (
          <RegenerateButton
            ticketId={ticketId}
            suggestionTypes={['summary']}
            isLoading={summarySuggestion.isLoading}
          />
        )}
        <Summary
          ticketId={ticketId}
          suggestion={summarySuggestion.suggestion}
          isLoading={summarySuggestion.isLoading}
        />
        <BetaSignUp />
      </>
    )
  }

  if (
    !sentimentSuggestion.suggestion.id &&
    !sentimentSuggestion.isLoading &&
    !summarySuggestion.suggestion.id &&
    !summarySuggestion.isLoading &&
    !tagsSuggestion.suggestion.id &&
    !tagsSuggestion.isLoading
  ) {
    return (
      <>
        <span className="grui flex text-center">
          Summarize the conversation, get tag suggestions, and detect your
          customer&apos;s sentiment.
        </span>
        <Button
          className="grui flex mt-5 ml-auto mr-auto px-6"
          type="tertiary"
          size="small"
          onClick={onGenerateFullOverviewClick}
          css={baseStyles.actionButton}
        >
          Generate AI Overview
        </Button>
      </>
    )
  }

  if (
    !sentimentSuggestion.suggestion.id &&
    !sentimentSuggestion.isLoading &&
    !tagsSuggestion.suggestion.id &&
    !tagsSuggestion.isLoading
  ) {
    return (
      <>
        <RegenerateButton
          ticketId={ticketId}
          suggestionTypes={['summary']}
          isLoading={summarySuggestion.isLoading}
        />
        <Summary
          ticketId={ticketId}
          suggestion={summarySuggestion.suggestion}
          isLoading={summarySuggestion.isLoading}
        />
        <Button
          className="grui flex mt-5 ml-auto mr-auto px-6"
          type="tertiary"
          size="small"
          onClick={onGenerateReducedOverviewClick}
          css={baseStyles.actionButton}
        >
          Generate sentiment and suggested tags
        </Button>
      </>
    )
  }

  return (
    <>
      <RegenerateButton
        ticketId={ticketId}
        suggestionTypes={['sentiment', 'summary', 'tags']}
        isLoading={
          sentimentSuggestion.isLoading ||
          summarySuggestion.isLoading ||
          tagsSuggestion.isLoading
        }
      />
      <Sentiment
        ticketId={ticketId}
        suggestion={sentimentSuggestion.suggestion}
        isLoading={sentimentSuggestion.isLoading}
      />
      <Summary
        ticketId={ticketId}
        suggestion={summarySuggestion.suggestion}
        isLoading={summarySuggestion.isLoading}
      />
      <SuggestedTags
        ticketId={ticketId}
        accountId={accountId}
        suggestion={tagsSuggestion.suggestion}
        isLoading={tagsSuggestion.isLoading}
        isLoadingTagNames={tagsSuggestion.isLoadingTagNames}
        isLoadedTagNames={tagsSuggestion.isLoadedTagNames}
      />
    </>
  )
}

export default memo(Content)
